/**
 * SOVRANOVA — VENT View v10
 * Auto-connects Hume EVI. Home button added. No "Connect" button required.
 */
import { useState, useRef, useEffect, useCallback } from 'react'
import { VoiceProvider, useVoice, VoiceReadyState } from '@humeai/voice-react'
import { api } from '../../services/api'
import { getHumeVoiceId } from '../../services/voices'
import { resolveRegion, getCrisisResources } from '../../services/crisisResources'
import { useWakeLock, useVoiceResume } from '../../hooks/useSessionResilience'
import { useChatAutoScroll } from '../../hooks/useChatAutoScroll'

// Hume-mode voice VENT is OFF for beta wave 1 (founder decision, 4 Sep 2026):
// its crisis screening runs AFTER Hume has started answering, is dropped on a
// 429/5xx, and does not run until /vent/start resolves. Until that path is
// rebuilt, VENT renders the text fallback below, which screens every message
// before any reply. Gated by VITE_FEATURE_HUME_VENT (build) AND the server's
// FEATURE_HUME_VENT (the token request is refused with hume_vent_disabled).
const FEATURE_HUME_VENT = import.meta.env.VITE_FEATURE_HUME_VENT === 'true'

const buildVentSystemPrompt = (res) => `You are SOVRANOVA — a deeply warm, emotionally intelligent wellbeing companion.
Right now you are in VENT mode. Your entire purpose is to make this person feel completely heard and safe.

VOICE: Warm. Calm. Unhurried. Like a trusted friend who has known them for years.

PHASE 1 — RECEIVE (first messages): Pure receiving. Short, warm responses only.
- Mirror emotion: "That sounds like you've been carrying something incredibly heavy."
- Validate: "Of course you feel that way. Anyone would."
- Short responses. "I hear you." / "Keep going, I'm right here."
- NEVER give advice yet. NEVER reframe. Just receive.

PHASE 2 — REFLECT (emotion softening): Gently reflect what you notice underneath.
- "What I'm hearing underneath all of this is..."
- "Where do you feel that in your body right now?"

PHASE 3 — INVITE (only when they ask "what do I do?"):
- "Would you like to explore what might be underneath this? Only if you're ready."

NEVER: clinical language. NEVER: "I understand how you feel". NEVER: "at least..." or "everything happens for a reason."

IF THEY SIGNAL CRISIS, SELF-HARM, OR SUICIDAL THOUGHTS:
- Stay. Slow down and stay right here with them — do not withdraw, end the chat, or hand them off coldly.
- Validate the feeling first: the pain is real, and they are never a burden for sharing it.
- NEVER ask about, suggest, or explore method, means, plan, or timing. Give no instructions of any kind.
- NEVER counsel, diagnose, or treat — you are a companion, not a clinician.
- Warmly bridge to real human help as care, not dismissal: ${res.primary.label} ${res.primary.number}, or ${res.emergency.label} ${res.emergency.number} if they are in immediate danger (${res.region}). Offer to stay while they reach out.
- Do not minimise, rush, or moralise. Keep them company.

Use their name if you know it. You are their safe place.`

const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition

function browserSpeak(text, onDone) {
  if (!window.speechSynthesis) { onDone?.(); return }
  window.speechSynthesis.cancel()
  const utt = new SpeechSynthesisUtterance(text)
  const trySpeak = () => {
    const voices = window.speechSynthesis.getVoices()
    const v = voices.find(v => v.name.includes('Zira') || v.name.includes('Aria') || v.name.includes('Jenny')) || voices.find(v => v.lang?.startsWith('en'))
    if (v) utt.voice = v
    utt.rate = 0.88; utt.pitch = 1.05
    utt.onend = () => onDone?.(); utt.onerror = () => onDone?.()
    window.speechSynthesis.speak(utt)
  }
  if (window.speechSynthesis.getVoices().length > 0) trySpeak()
  else window.speechSynthesis.onvoiceschanged = trySpeak
}

async function speakWithElevenLabs(text, onDone) {
  try {
    const res = await api.post('/voice/speak', { text })
    if (!res?.audio) throw new Error('no audio')
    const binary = atob(res.audio); const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    const audio = new Audio(URL.createObjectURL(new Blob([bytes], { type: res.mimeType || 'audio/mpeg' })))
    audio.onended = () => onDone?.(); audio.onerror = () => browserSpeak(text, onDone)
    await audio.play()
  } catch { browserSpeak(text, onDone) }
}

// ── Hume EVI inner — speaks greeting on open, one tap to activate mic ────────
function HumeVentInner({ humeToken, configId, onClose, onHome, ventIdRef, crisis }) {
  const { connect, disconnect, readyState, messages, micFft, pauseAssistant, muteAudio, setVolume, isPlaying, mute, unmute } = useVoice()
  const res = getCrisisResources(resolveRegion())
  const [connected, setConnected] = useState(false)
  const [started, setStarted] = useState(false)
  const [error, setError] = useState(null)
  const [greeted, setGreeted] = useState(false)
  const bottomRef = useRef(null)
  const crisisSpokenRef = useRef(false)

  // When the parallel safety screen flags a crisis, SILENCE Hume (pause + mute +
  // volume 0) so SOVRANOVA's authoritative crisis response is heard cleanly, then
  // speak it aloud once — mirrors useVoiceSafety on the other four voice surfaces.
  useEffect(() => {
    if (crisis?.response && !crisisSpokenRef.current) {
      crisisSpokenRef.current = true
      try { pauseAssistant && pauseAssistant() } catch (_) {}
      try { muteAudio && muteAudio() } catch (_) {}
      try { setVolume && setVolume(0) } catch (_) {}
      speakWithElevenLabs(crisis.response, () => {})
    }
  }, [crisis])

  useChatAutoScroll(bottomRef, messages)   // follow only while the reader is at the bottom

  // ECHO GUARD (same as Live): on speakerphone the mic hears SOVRANOVA's own
  // voice and it answers itself. Mute the mic while the assistant is speaking.
  useEffect(() => {
    if (!connected) return
    if (isPlaying) { try { mute() } catch (_) {}; return }
    const t = setTimeout(() => { try { unmute() } catch (_) {} }, 400)
    return () => clearTimeout(t)
  }, [isPlaying, connected])

  // Speak the greeting aloud on open — pure audio OUT via TTS, independent of
  // the microphone and of Hume. This is how Vent spoke automatically before the
  // Hume migration. (iOS may hold audio until the first page interaction; the
  // greeting still shows on screen and will speak on the first tap if so.)
  const GREETING = "I'm here with you. Take a breath. Tell me what's on your mind."
  useEffect(() => {
    let cancelled = false
    speakWithElevenLabs(GREETING, () => {})
    setGreeted(true)
    return () => { cancelled = true; window.speechSynthesis?.cancel() }
  }, [])

  // Mic activation. iOS requires the mic to be opened SYNCHRONOUSLY inside the
  // tap, before any await — so we prime getUserMedia first (captures the
  // gesture), then hand off to Hume's connect(). This fixes "Could not start
  // the microphone" on iOS Safari.
  const activate = useCallback(async () => {
    if (started && connected) return
    setStarted(true); setError(null)
    // 1) Prime the mic within the gesture and KEEP it open through connect().
    // The Hume SDK opens its own stream during connect(); releasing ours first
    // made Edge/Chromium hit an open-close-open teardown race (NotReadableError
    // / "Could not start audio source"). Holding it keeps the device warm.
    let primeStream = null
    try {
      primeStream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (e) {
      setError(e?.name === 'NotAllowedError' ? 'mic_blocked' : 'mic_failed')
      return
    }
    // 2) Now connect Hume — permission is already granted, gesture satisfied
    try {
      await connect({
        auth: { type: 'accessToken', value: humeToken },
        ...(configId ? { configId } : {}),
        ...(getHumeVoiceId() ? { voiceId: getHumeVoiceId() } : {}),
        sessionSettings: { type: 'session_settings', systemPrompt: buildVentSystemPrompt(res) },
      })
      setConnected(true); setError(null)
      // Re-speak the greeting through the live session context if it was held
      if (!greeted) speakWithElevenLabs(GREETING, () => {})
    } catch (e) {
      setConnected(false)
      setError('mic_failed')
    } finally {
      primeStream?.getTracks().forEach(t => t.stop())
    }
  }, [connect, started, connected, greeted, humeToken])

  // Reopen the EVI socket after the phone slept. Mints a fresh Hume token first
  // — the original is short-lived and is usually stale by the time we're back.
  const reopen = useCallback(async () => {
    // Prime the mic FIRST, synchronously inside the resume tap — same rule as
    // activate(). Fetching the token first consumed the iOS gesture window, so
    // Hume's own getUserMedia failed and "tap to pick up" silently did nothing
    // (Wave 1, 20 Aug, iPhone). Hold the stream through connect().
    let primeStream = null
    try { primeStream = await navigator.mediaDevices.getUserMedia({ audio: true }) } catch (_) { /* try anyway */ }
    let token = humeToken
    try {
      const r = await api.post('/hume/token', { purpose: 'vent' })
      if (r?.accessToken) token = r.accessToken
    } catch { /* fall back to the token we already hold */ }
    try {
      await connect({
        auth: { type: 'accessToken', value: token },
        ...(configId ? { configId } : {}),
        ...(getHumeVoiceId() ? { voiceId: getHumeVoiceId() } : {}),
        sessionSettings: { type: 'session_settings', systemPrompt: buildVentSystemPrompt(res) },
      })
    } finally {
      primeStream?.getTracks().forEach(t => t.stop())
    }
  }, [connect, configId, humeToken])

  // Keep the screen awake while venting, and recover if it slept anyway.
  useWakeLock(connected)
  const { suspended, resuming, resume } = useVoiceResume({
    active: connected,
    isOpen: () => readyState === VoiceReadyState.OPEN,
    reconnect: reopen,
  })

  useEffect(() => () => { disconnect() }, [])

  const close = () => { disconnect(); if (ventIdRef.current) api.post('/vent/end', { ventId: ventIdRef.current }).catch(() => {}); onClose?.() }
  const home  = () => { disconnect(); if (ventIdRef.current) api.post('/vent/end', { ventId: ventIdRef.current }).catch(() => {}); onHome?.() }

  const isConnecting = readyState === VoiceReadyState.CONNECTING
  const chatMessages = messages.filter(m => m.type === 'user_message' || m.type === 'assistant_message')

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.backBtn} onClick={close}>&#8592; Back</button>
        <div style={s.badge}>
          <span style={{ ...s.dot, animationPlayState: connected ? 'running' : 'paused' }} />
          VENT {suspended ? '· Paused' : connected ? '· Listening' : isConnecting ? '· Connecting...' : ''}
        </div>
        <button style={s.homeBtn} onClick={home}>⌂ Home</button>
      </div>
      <div style={s.msgs}>
        {chatMessages.length === 0 && (
          <div style={s.intro}>
            <p style={s.greeting}>I'm here with you.</p>
            <p style={s.subprompt}>
              {connected ? 'Just speak — I hear how you feel, not just what you say.'
                : error === 'mic_blocked' ? 'Microphone is blocked. Allow it (see below), then tap again.'
                : error === 'mic_failed' ? 'The mic didn\'t start. Tap to try again.'
                : isConnecting ? 'Connecting...'
                : 'Tap below when you\'re ready. Then just talk — it stays hands-free.'}
            </p>
            {!connected && !isConnecting && (
              <button style={s.beginBtn} onClick={activate}>
                {started ? 'Tap to try again' : 'Tap to begin'}
              </button>
            )}
            <p style={s.tapPrompt}>Powered by Hume EVI 3 — emotion-aware voice</p>
          </div>
        )}
        {chatMessages.map((m, i) => {
          const isUser = m.type === 'user_message'; const text = m.message?.content
          if (!text) return null
          return (
            <div key={i} style={{ display:'flex', justifyContent: isUser?'flex-end':'flex-start', gap:10 }}>
              {!isUser && <div style={s.avatar}>S</div>}
              <div style={{ ...s.bubble, ...(isUser ? s.userBubble : s.aiBubble) }}>{text}</div>
            </div>
          )
        })}
        {crisis && (
          <div style={s.crisis}>
            <p style={{ fontWeight:600, marginBottom:'0.4rem' }}>You don't have to face this alone.</p>
            {crisis.response && <div style={{ whiteSpace:'pre-wrap', marginBottom:'0.4rem' }}>{crisis.response}</div>}
            <a href={`tel:${res.primary.tel}`} style={s.cLink}>{res.primary.label} {res.primary.number}</a>
            <a href={`tel:${res.emergency.tel}`} style={s.cLink}>{res.emergency.label} {res.emergency.number}</a>
            <p style={{ ...s.cLink, opacity:0.6, fontSize:'0.72rem' }}>{res.region} · 24/7</p>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      {connected && micFft && (
        <div style={s.fftWrap}>{micFft.slice(0,20).map((v,i) => <div key={i} style={{ ...s.fftBar, height:`${4+v*40}px` }} />)}</div>
      )}
      {error === 'mic_blocked' && (
        <p style={s.err}>Microphone blocked — click 🔒 in address bar → Site settings → Microphone → Allow → refresh.</p>
      )}
      <div style={s.controls}>
        {connected && suspended && (
          <button style={s.disconnectBtn} onClick={resume} disabled={resuming}>
            {resuming ? 'Reconnecting…' : '🎙 Tap to pick up where we left off'}
          </button>
        )}
        {connected && <button style={s.disconnectBtn} onClick={() => { disconnect(); setConnected(false) }}>&#9632; End Session</button>}
        <p style={s.hint}>{connected ? 'Speak freely — I respond to your emotions, not just your words' : 'One tap to start — then it\'s hands-free'}</p>
      </div>
    </div>
  )
}

// ── Fallback (no Hume) ────────────────────────────────────────────────────────
function FallbackVent({ onClose, onHome }) {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [recording, setRecording] = useState(false)
  const [crisis, setCrisis] = useState(false)
  const [voiceError, setVoiceError] = useState(null)
  const [welcomed, setWelcomed] = useState(false)
  const res = getCrisisResources(resolveRegion())
  const PROMPTS = ["Just let it out.", "What's been heaviest today?", "What can't you say out loud?"]
  const [prompt] = useState(() => PROMPTS[Math.floor(Math.random() * PROMPTS.length)])
  const bottomRef = useRef(null); const textareaRef = useRef(null)
  const ventIdRef = useRef(null); const historyRef = useRef([])
  const currentAudio = useRef(null); const recognitionRef = useRef(null)
  const holdingRef = useRef(false)

  useEffect(() => { historyRef.current = messages.map(m => ({ role: m.role, content: m.content })) }, [messages])
  useEffect(() => {
    api.post('/vent/start', {}).then(({ ventId: id }) => { ventIdRef.current = id }).catch(() => {})
    return () => { currentAudio.current?.pause(); window.speechSynthesis?.cancel(); recognitionRef.current?.stop() }
  }, [])
  useChatAutoScroll(bottomRef, messages)   // follow only while the reader is at the bottom

  const playWelcome = useCallback(async () => {
    if (welcomed) return; setWelcomed(true)
    speakWithElevenLabs("I'm here with you. Take a breath. Tell me what's on your mind.", () => {})
  }, [welcomed])

  const sendMessage = useCallback(async (text) => {
    if (!text?.trim() || !ventIdRef.current) return
    setLoading(true)
    setMessages(prev => [...prev, { id: Date.now(), role: 'user', content: text }]); setInput('')
    try {
      const result = await api.post('/vent/message', { message: text, ventId: ventIdRef.current, history: historyRef.current, region: resolveRegion() })
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: result.response }])
      if (result.crisisDetected) setCrisis(true)
      speakWithElevenLabs(result.response, () => {})
    } catch { setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: "I'm still here. Try again." }]) }
    finally { setLoading(false) }
  }, [])

  const startHold = useCallback(async (e) => {
    e?.preventDefault(); if (loading || recording) return
    holdingRef.current = true; setVoiceError(null); playWelcome()
    if (!SpeechRecognitionAPI) { setVoiceError('Live speech not supported — type instead.'); return }
    const r = new SpeechRecognitionAPI(); r.continuous = true; r.interimResults = true; r.lang = 'en-AU'
    let final = ''
    r.onresult = (ev) => {
      let interim = ''
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        if (ev.results[i].isFinal) final += ev.results[i][0].transcript + ' '
        else interim = ev.results[i][0].transcript
      }
      setInput((final + interim).trim())
    }
    r.onerror = (ev) => { if (ev.error === 'not-allowed') setVoiceError('Mic denied.'); setRecording(false); holdingRef.current = false }
    r.onend = () => setRecording(false)
    recognitionRef.current = r
    try { r.start(); setRecording(true) } catch { setVoiceError('Could not start mic.') }
  }, [loading, recording, playWelcome])

  const stopHold = useCallback(() => { if (!holdingRef.current) return; holdingRef.current = false; recognitionRef.current?.stop(); setRecording(false) }, [])
  const handleClose = () => { if (ventIdRef.current) api.post('/vent/end', { ventId: ventIdRef.current }).catch(() => {}); onClose?.() }
  const handleHome  = () => { if (ventIdRef.current) api.post('/vent/end', { ventId: ventIdRef.current }).catch(() => {}); onHome?.() }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.backBtn} onClick={handleClose}>&#8592; Back</button>
        <div style={s.badge}><span style={{ ...s.dot, animationPlayState: recording ? 'running' : 'paused' }} />VENT</div>
        <button style={s.homeBtn} onClick={handleHome}>⌂ Home</button>
      </div>
      <div style={s.msgs}>
        {messages.length === 0 && <div style={s.intro}><p style={s.greeting}>I'm listening.</p><p style={s.subprompt}>{prompt}</p></div>}
        {messages.map(m => (
          <div key={m.id} style={{ display:'flex', justifyContent: m.role==='user'?'flex-end':'flex-start', gap:10 }}>
            {m.role==='assistant' && <div style={s.avatar}>S</div>}
            <div style={{ ...s.bubble, ...(m.role==='user' ? s.userBubble : s.aiBubble) }}>{m.content}</div>
          </div>
        ))}
        {loading && <div style={{ display:'flex', gap:10 }}><div style={s.avatar}>S</div><div style={{ ...s.bubble, ...s.aiBubble, display:'flex', gap:5, alignItems:'center' }}>{[0,1,2].map(i=><span key={i} style={{ ...s.typeDot, animationDelay:`${i*0.15}s` }}/>)}</div></div>}
        {crisis && <div style={s.crisis}><p style={{ fontWeight:600, marginBottom:'0.4rem' }}>You don't have to face this alone.</p><a href={`tel:${res.primary.tel}`} style={s.cLink}>{res.primary.label} {res.primary.number}</a><a href={`tel:${res.emergency.tel}`} style={s.cLink}>{res.emergency.label} {res.emergency.number}</a></div>}
        <div ref={bottomRef} />
      </div>
      {voiceError && <p style={s.err}>{voiceError}</p>}
      {recording && <p style={s.hint}>&#9679; Listening — release, then press Send</p>}
      <div style={s.inputRow}>
        <button style={{ ...s.micBtn, ...(recording ? s.micActive : {}) }} onMouseDown={startHold} onMouseUp={stopHold} onMouseLeave={stopHold} onTouchStart={startHold} onTouchEnd={stopHold} onTouchCancel={stopHold} disabled={loading}>{recording ? '●' : '🎙'}</button>
        <textarea ref={textareaRef} style={{ ...s.ta, ...(recording ? s.taLive : {}) }} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input.trim()) } }} placeholder={recording ? 'Listening...' : 'Or type and press Send'} rows={1} disabled={loading} />
        <button style={{ ...s.sendBtn, opacity:(!input.trim()||loading)?0.25:1 }} onClick={() => sendMessage(input.trim())} disabled={!input.trim()||loading}>send</button>
      </div>
      <style>{`@keyframes vp{0%,100%{opacity:.4}50%{opacity:1}}@keyframes bd{0%,100%{opacity:.3;transform:translateY(0)}50%{opacity:1;transform:translateY(-4px)}}`}</style>
    </div>
  )
}

// ── Main export ───────────────────────────────────────────────────────────────
export function VentView({ onClose, onHome }) {
  const [humeToken, setHumeToken] = useState(null)
  const [humeChecked, setHumeChecked] = useState(false)
  const [configId, setConfigId] = useState(null)
  const [crisis, setCrisis] = useState(null)
  const ventIdRef = useRef(null)

  useEffect(() => {
    if (!FEATURE_HUME_VENT) { setHumeToken(false); setHumeChecked(true); return }
    api.post('/hume/token', { purpose: 'vent' })
      .then(({ accessToken }) => { setHumeToken(accessToken); setHumeChecked(true) })
      .catch(() => { setHumeToken(false); setHumeChecked(true) })
    // The EVI config (set in the Hume dashboard, HUME_CONFIG_ID) selects the
    // VOICE. Without it EVI falls back to a default voice that can sound robotic.
    // Optional: if no config id is set, behaviour is unchanged.
    api.get('/hume/config')
      .then(({ configId }) => { if (configId) setConfigId(configId) })
      .catch(() => {})
  }, [])

  // Start the vent session for the Hume path. Without this, ventIdRef stayed
  // null forever, so the onMessage pipeline below never fired — meaning NO
  // crisis screening, NO message persistence, and NO end-of-session memory
  // for Hume voice vents. This restores all three.
  useEffect(() => {
    if (!humeToken) return
    api.post('/vent/start', {})
      .then(({ ventId }) => { ventIdRef.current = ventId })
      .catch(() => {})
  }, [humeToken])

  if (!humeChecked) return <div style={{ ...s.page, alignItems:'center', justifyContent:'center' }}><div style={{ fontSize:'2rem', opacity:0.3 }}>◎</div></div>

  if (humeToken) {
    return (
      <VoiceProvider
        onMessage={msg => {
          if (msg.type === 'user_message' && ventIdRef.current) {
            api.post('/vent/message', { message: msg.message?.content, ventId: ventIdRef.current, history: [], humeMode: true, region: resolveRegion() })
              .then(r => { if (r?.crisisDetected) setCrisis(r) })
              .catch(() => {})
          }
        }}>
        <HumeVentInner humeToken={humeToken} configId={configId} onClose={onClose} onHome={onHome} ventIdRef={ventIdRef} crisis={crisis} />
      </VoiceProvider>
    )
  }
  return <FallbackVent onClose={onClose} onHome={onHome} />
}

// ── Styles ────────────────────────────────────────────────────────────────────
// Organic restyle (Board 3 "V1", 2026-08-13): the sage-led "quiet room" —
// deep warm ink with a soft sage glow. Crisis block deliberately KEEPS its
// red-tinted styling (safety chrome is never softened). Values only.
const s = {
  page:         { display:'flex', flexDirection:'column', height:'100%', background:'radial-gradient(circle at 50% 112%, rgba(122,138,94,.30), transparent 58%), #2e2b25' },
  header:       { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.875rem 1.25rem', borderBottom:'1px solid rgba(245,234,216,.08)', background:'rgba(46,43,37,.97)', flexShrink:0 },
  backBtn:      { background:'transparent', border:'none', color:'rgba(245,234,216,.45)', fontSize:'0.875rem', cursor:'pointer' },
  homeBtn:      { background:'rgba(245,234,216,.08)', border:'none', color:'rgba(245,234,216,.7)', borderRadius:'999px', padding:'0.35rem 0.8rem', fontSize:'0.78rem', cursor:'pointer', fontWeight:600 },
  badge:        { display:'flex', alignItems:'center', gap:'0.5rem', fontSize:'0.72rem', fontWeight:700, letterSpacing:'0.14em', color:'#ccdbb2' },
  dot:          { width:8, height:8, borderRadius:'50%', background:'#7a8a5e', boxShadow:'0 0 0 4px rgba(122,138,94,.25)', display:'block', animation:'vp 1.2s ease-in-out infinite' },
  msgs:         { flex:1, overflowY:'auto', padding:'1.5rem 1.25rem', display:'flex', flexDirection:'column', gap:'1rem' },
  intro:        { textAlign:'center', paddingTop:'2.5rem', display:'flex', flexDirection:'column', gap:'0.5rem' },
  greeting:     { fontSize:'1.45rem', fontWeight:600, color:'#f5ead8' },
  subprompt:    { fontSize:'1rem', color:'rgba(245,234,216,.5)', lineHeight:1.7 },
  tapPrompt:    { fontSize:'0.75rem', color:'rgba(204,219,178,.6)', marginTop:'0.5rem' },
  beginBtn:     { marginTop:'1.25rem', padding:'1rem 2.6rem', borderRadius:'999px', border:'none', background:'#7a8a5e', color:'#fff', fontSize:'1.05rem', fontWeight:600, cursor:'pointer', alignSelf:'center', boxShadow:'0 4px 20px rgba(122,138,94,.35)' },
  avatar:       { width:32, height:32, borderRadius:'50%', flexShrink:0, background:'rgba(122,138,94,.35)', border:'none', display:'flex', alignItems:'center', justifyContent:'center', color:'#e1eecc', fontSize:'0.75rem', fontWeight:700 },
  bubble:       { maxWidth:'78%', padding:'0.875rem 1.125rem', borderRadius:'18px', fontSize:'0.975rem', lineHeight:1.7, whiteSpace:'pre-wrap' },
  userBubble:   { background:'rgba(198,113,57,.35)', border:'none', color:'#f5ead8', borderBottomRightRadius:'6px' },
  aiBubble:     { background:'rgba(122,138,94,.35)', border:'none', color:'#f0fae1', borderBottomLeftRadius:'6px' },
  typeDot:      { width:6, height:6, borderRadius:'50%', background:'rgba(245,234,216,.5)', display:'inline-block', animation:'bd 1s ease-in-out infinite' },
  crisis:       { background:'rgba(192,57,43,.14)', border:'1.5px solid rgba(192,57,43,.35)', borderRadius:'18px', padding:'1rem 1.25rem', display:'flex', flexDirection:'column', gap:'0.35rem', fontSize:'0.875rem', color:'#f5ead8' },
  cLink:        { color:'#ffc6a5', textDecoration:'none', display:'block', padding:'0.15rem 0' },
  err:          { textAlign:'center', fontSize:'0.78rem', color:'#ffc6a5', padding:'0.25rem 1.25rem', lineHeight:1.55 },
  hint:         { textAlign:'center', fontSize:'0.75rem', color:'rgba(245,234,216,.38)', padding:'0.5rem 1.25rem 0', lineHeight:1.5 },
  inputRow:     { display:'flex', alignItems:'flex-end', gap:'0.625rem', padding:'0.75rem 1.25rem 1.25rem', borderTop:'1px solid rgba(245,234,216,.08)', background:'rgba(39,36,31,.97)', flexShrink:0 },
  micBtn:       { width:48, height:48, borderRadius:'50%', border:'2px solid rgba(225,238,204,.3)', background:'rgba(245,234,216,.06)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.2rem', flexShrink:0, transition:'all .15s', userSelect:'none' },
  micActive:    { background:'#7a8a5e', border:'2px solid #7a8a5e', transform:'scale(1.1)' },
  ta:           { flex:1, padding:'0.75rem 1.1rem', background:'rgba(245,234,216,.07)', border:'1px solid rgba(245,234,216,.12)', borderRadius:'18px', color:'#f5ead8', fontSize:'0.975rem', fontFamily:'inherit', resize:'none', minHeight:44, maxHeight:140, lineHeight:1.5 },
  taLive:       { border:'1px solid rgba(122,138,94,.6)', background:'rgba(122,138,94,.12)' },
  sendBtn:      { width:48, height:44, borderRadius:'999px', flexShrink:0, background:'#7a8a5e', color:'white', border:'none', cursor:'pointer', fontSize:'0.75rem', fontWeight:700, letterSpacing:'0.04em', transition:'opacity .2s' },
  fftWrap:      { display:'flex', alignItems:'center', gap:3, height:40, padding:'0 1.25rem', justifyContent:'center' },
  fftBar:       { width:4, borderRadius:3, background:'rgba(225,238,204,.75)', transition:'height 0.05s' },
  controls:     { padding:'0.75rem 1.25rem 1.5rem', display:'flex', flexDirection:'column', gap:'0.75rem', alignItems:'center' },
  disconnectBtn:{ width:'100%', maxWidth:360, padding:'1rem', background:'rgba(245,234,216,.08)', border:'none', color:'rgba(245,234,216,.75)', borderRadius:'999px', fontSize:'1rem', fontWeight:600, cursor:'pointer' },
}
